import {
  type ReactNode,
  type JSX,
  useMemo,
  useEffect,
  useState,
  createContext,
  useContext,
} from 'react';
import {
  type Theme,
  getLightTheme,
  paletteTokens,
  BrandingContext,
  type BrandingContextValue,
} from '@doxyme/ui/theme';
import { colord, extend } from 'colord';
import mixPlugin from 'colord/plugins/mix';
import {
  useGetCustomBrandingQuery,
  useGetCustomBrandingBySlugQuery,
} from '../services/branding/operations.generated';
import { fetchCustomBrandingBySlug } from '../services/branding/fetchCustomBrandingBySlug';
import { useTenantId } from '../hooks/useAuth';
import { FullPageSpinner } from '../components/global/fullPageSpinner';
import { useAppDispatch } from '../hooks/useReduxToolkit';
import { initializeAsync as initializeBranding } from '../features/branding/branding.slice';
import { getGrowthBookConfigLoader } from '@doxyme/config';

export const getClinicBrandingEnabled = (): boolean =>
  getGrowthBookConfigLoader().getOptional(
    'feat_cs_16779_clinic_branding_settings_enabled',
  ) === true;

extend([mixPlugin]);

const lightTheme = getLightTheme() as Theme;

/** Fixed overrides of the theme, to apply along with any custom branding. */
const themeOverrideWhenCustom = {
  background: {
    default: {
      backend: lightTheme.background.default.backend,
    },
  },
};

const brandingToPalette = (
  primaryColor?: string | null,
): BrandingContextValue['overridePalette'] | undefined => {
  const primary =
    typeof primaryColor === 'string' ? colord(primaryColor) : undefined;

  if (!primary) {
    return undefined;
  }

  return {
    dynamicContrast: {
      primary: {
        100: lightTheme.palette.getContrastText(primary.toRgbString()),
      },
    },
    brand: {
      primary: {
        25: primary.lighten(0.15).toRgbString(),
        50: primary.lighten(0.3).toRgbString(),
        100: primary.toRgbString(),
        150: primary.darken(0.1).toRgbString(),
        200: primary.darken(0.2).toRgbString(),
        250: primary.darken(0.25).toRgbString(),
      },
    },
  };
};

type BrandingAssets = {
  logoUrl?: string;
  faviconUrl?: string;
  hasCustomBranding: boolean;
  clinicName?: string;
  isLoading: boolean;
};

const BrandingAssetsContext = createContext<BrandingAssets>({
  hasCustomBranding: false,
  isLoading: true,
});

/** Hook to access branding assets (logos, favicons) */
export const useBrandingAssets = (): BrandingAssets => {
  return useContext(BrandingAssetsContext);
};

type BrandingEntitlement = {
  isEntitled: boolean;
  isLoading: boolean;
};

type BrandingProviderProps = {
  entitlement: BrandingEntitlement;
  roomSlug?: string;
  children: ReactNode;
};

/**
 * Branding provider that fetches branding by slug only. Does not use auth hooks
 * or RTK Query, so it is safe to render above the Frontegg and Redux providers
 * (e.g. on the clinic sign-in page where we have clinicSlug in the URL).
 */
export const BrandingProviderBySlug = ({
  children,
  entitlement,
  roomSlug,
}: {
  entitlement: BrandingEntitlement;
  roomSlug: string;
  children: ReactNode;
}): JSX.Element => {
  const { isEntitled, isLoading: isLoadingEntitlements } = entitlement;
  const [customBranding, setCustomBranding] = useState<Awaited<
    ReturnType<typeof fetchCustomBrandingBySlug>
  > | null>(null);
  const [isLoadingSlug, setIsLoadingSlug] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setCustomBranding(null);
    setIsLoadingSlug(true);
    void fetchCustomBrandingBySlug(roomSlug)
      .then((data) => {
        if (!cancelled) {
          setCustomBranding(data);
        }
        return data;
      })
      .catch(() => {
        if (!cancelled) {
          setCustomBranding(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingSlug(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [roomSlug]);

  const hasCustomBranding = Boolean(
    customBranding?.faviconUrl ||
      customBranding?.logoUrl ||
      customBranding?.primaryColor,
  );

  const brandingValue = useMemo((): BrandingContextValue => {
    if (isLoadingSlug || isLoadingEntitlements) {
      return {
        overrideTheme: themeOverrideWhenCustom,
        overridePalette: brandingToPalette(paletteTokens.neutral.grey[150]),
      };
    }

    if (!hasCustomBranding || !isEntitled) {
      return {
        overrideTheme: themeOverrideWhenCustom,
        overridePalette: undefined,
      };
    }

    return {
      overrideTheme: themeOverrideWhenCustom,
      overridePalette: brandingToPalette(customBranding?.primaryColor),
    };
  }, [
    isLoadingSlug,
    customBranding,
    hasCustomBranding,
    isEntitled,
    isLoadingEntitlements,
  ]);

  const brandingAssets = useMemo((): BrandingAssets => {
    if (isLoadingSlug || isLoadingEntitlements) {
      return {
        hasCustomBranding: false,
        isLoading: true,
      };
    }

    if (!hasCustomBranding || !isEntitled) {
      return {
        logoUrl: '/v2/images/doxyLogoSmall.svg',
        faviconUrl: '/v2/images/faviconlogo.ico',
        hasCustomBranding: false,
        clinicName: undefined,
        isLoading: false,
      };
    }

    return {
      faviconUrl: customBranding?.faviconUrl ?? undefined,
      logoUrl: customBranding?.logoUrl ?? undefined,
      hasCustomBranding: true,
      clinicName: customBranding?.tenantName ?? undefined,
      isLoading: false,
    };
  }, [
    isLoadingSlug,
    customBranding,
    hasCustomBranding,
    isEntitled,
    isLoadingEntitlements,
  ]);

  if (isLoadingSlug) {
    return <FullPageSpinner />;
  }

  return (
    <BrandingContext.Provider value={brandingValue}>
      <BrandingAssetsContext.Provider value={brandingAssets}>
        {children}
      </BrandingAssetsContext.Provider>
    </BrandingContext.Provider>
  );
};

export const BrandingProvider = ({
  children,
  entitlement,
  roomSlug,
}: BrandingProviderProps): JSX.Element => {
  const tenantId = useTenantId();
  const dispatch = useAppDispatch();

  const { isEntitled, isLoading: isLoadingEntitlements } = entitlement;

  useEffect(() => {
    if (tenantId) {
      void dispatch(initializeBranding(tenantId));
    }
  }, [dispatch, tenantId]);

  const { data: tenantData, isLoading: isLoadingTenant } =
    useGetCustomBrandingQuery(undefined, {
      skip: !tenantId || !!roomSlug,
    });

  const { data: slugData, isLoading: isLoadingSlug } =
    useGetCustomBrandingBySlugQuery(
      { input: { slug: roomSlug ?? '' } },
      { skip: !roomSlug },
    );

  const customBranding = roomSlug
    ? slugData?.Tenant?.customBrandingBySlug
    : tenantData?.Tenant?.customBranding;
  const isLoading = roomSlug ? isLoadingSlug : isLoadingTenant;

  const hasCustomBranding = Boolean(
    customBranding?.faviconUrl ||
      customBranding?.logoUrl ||
      customBranding?.primaryColor,
  );

  const brandingValue = useMemo((): BrandingContextValue => {
    if (isLoading || isLoadingEntitlements) {
      return {
        overrideTheme: themeOverrideWhenCustom,
        overridePalette: brandingToPalette(paletteTokens.neutral.grey[150]),
      };
    }

    if (!hasCustomBranding || !isEntitled) {
      return {
        overrideTheme: themeOverrideWhenCustom,
        overridePalette: undefined,
      };
    }

    return {
      overrideTheme: themeOverrideWhenCustom,
      overridePalette: brandingToPalette(customBranding?.primaryColor),
    };
  }, [
    isLoading,
    customBranding,
    hasCustomBranding,
    isEntitled,
    isLoadingEntitlements,
  ]);

  const brandingAssets = useMemo((): BrandingAssets => {
    if (isLoading || isLoadingEntitlements) {
      return {
        hasCustomBranding: false,
        isLoading: true,
      };
    }

    if (!hasCustomBranding || !isEntitled) {
      return {
        logoUrl: '/v2/images/doxyLogoSmall.svg',
        faviconUrl: '/v2/images/faviconlogo.ico',
        hasCustomBranding: false,
        clinicName: undefined,
        isLoading: false,
      };
    }

    return {
      faviconUrl: customBranding?.faviconUrl ?? undefined,
      logoUrl: customBranding?.logoUrl ?? undefined,
      hasCustomBranding: true,
      clinicName: customBranding?.tenantName ?? undefined,
      isLoading: false,
    };
  }, [
    isLoading,
    customBranding,
    hasCustomBranding,
    isEntitled,
    isLoadingEntitlements,
  ]);

  return (
    <BrandingContext.Provider value={brandingValue}>
      <BrandingAssetsContext.Provider value={brandingAssets}>
        {children}
      </BrandingAssetsContext.Provider>
    </BrandingContext.Provider>
  );
};
